/**
 * v1.1.90 — Instant call popup state machine.
 *
 * Wires the "Parle a un expert" button to a multi-state modal:
 *   ready -> calling -> success | fallback (booking) -> booked
 *
 * Pre-fills name/phone from the form session if available (form.php / form-service.php
 * step 1 fields). Falls back to inline input if no form context.
 *
 * Polls /meeting-instant-call-status every 2s for up to 30s to determine
 * if the rep answered. On timeout/no-answer/failed -> switch to fallback
 * state (mini calendar booking).
 */

const REST_NS = window.ProbotSoum && window.ProbotSoum.restUrl
	? window.ProbotSoum.restUrl
	: '/wp-json/probot-soum/v1/';
const NONCE = window.ProbotSoum && window.ProbotSoum.nonce ? window.ProbotSoum.nonce : '';

const STATES = ['ready', 'calling', 'success', 'fallback', 'booked'];
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000; // 30s — past Twilio's 25s rep dial timeout
const MONTHS_FR = ['janvier', 'fevrier', 'mars', 'avril', 'mai', 'juin', 'juillet', 'aout', 'septembre', 'octobre', 'novembre', 'decembre'];
const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW_SHORT_FR = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
const DOW_SHORT_EN = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

let pollTimer = null;
let pollStartTime = 0;
let currentCallSid = null;
// v1.1.101 — Email pre-rempli depuis le form Step 1 (#inEmail). Non visible
// dans le popup (popup ne demande que nom + tel), mais utilise pour le
// fallback booking qui necessite un email.
let prefilledEmail = '';
let bookingState = {
	viewMonth: null,
	availability: null, // { 'YYYY-MM-DD': ['10:00', '10:30', ...] }
	selectedDate: null,
	selectedSlot: null,
};

// v1.1.101 — Normalize un numero de telephone en E.164 (+1XXXXXXXXXX pour
// Amerique du Nord). Twilio rejette les non-E.164 avec un 400.
function normalizeToE164(raw) {
	if (!raw) return '';
	const cleaned = String(raw).replace(/[^0-9+]/g, '');
	if (!cleaned) return '';
	if (cleaned[0] === '+') return cleaned;
	// 10 chiffres = Amerique du Nord -> +1
	if (/^[0-9]{10}$/.test(cleaned)) return '+1' + cleaned;
	// 11 chiffres commencant par 1 = deja +1 sans le +
	if (/^1[0-9]{10}$/.test(cleaned)) return '+' + cleaned;
	// Sinon prefix + en esperant que ce soit international
	return '+' + cleaned;
}

function lang() {
	return window.ProbotSoum && window.ProbotSoum.lang === 'en' ? 'en' : 'fr';
}
function t(fr, en) { return lang() === 'en' ? en : fr; }
function monthName(m) { return (lang() === 'en' ? MONTHS_EN : MONTHS_FR)[m]; }
function dowShort() { return lang() === 'en' ? DOW_SHORT_EN : DOW_SHORT_FR; }
function isoDate(d) {
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');
	return `${y}-${m}-${day}`;
}

async function api(path, opts = {}) {
	opts.credentials = 'include';
	opts.headers = Object.assign(
		{ 'Content-Type': 'application/json', 'X-WP-Nonce': NONCE },
		opts.headers || {}
	);
	const r = await fetch(REST_NS + path.replace(/^\//, ''), opts);
	const data = await r.json().catch(() => ({}));
	return { status: r.status, data };
}

// v1.1.105 SECURITY — Fetch un token reCAPTCHA v3 pour l'action donnee.
// Resout TOUJOURS, jamais reject. Si provider != 'recaptcha' ou widget pas
// charge, retourne ''. Le backend fail-open si pas de provider configure.
async function fetchBotToken(action) {
	const cfg = (window.ProbotSoum || {});
	if (cfg.botProvider !== 'recaptcha' || !cfg.botSiteKey || !window.grecaptcha) {
		return '';
	}
	return new Promise((resolve) => {
		try {
			window.grecaptcha.ready(() => {
				try {
					window.grecaptcha.execute(cfg.botSiteKey, { action: action })
						.then((t) => resolve(t || ''))
						.catch(() => resolve(''));
				} catch (e) { resolve(''); }
			});
		} catch (e) { resolve(''); }
	});
}

function setState(name) {
	if (!STATES.includes(name)) return;
	const overlay = document.getElementById('probotInstantCallOverlay');
	if (!overlay) return;
	overlay.querySelectorAll('[data-pic-state]').forEach((el) => {
		el.hidden = el.dataset.picState !== name;
	});
}

function openModal() {
	const overlay = document.getElementById('probotInstantCallOverlay');
	if (!overlay) return;
	overlay.hidden = false;
	document.body.style.overflow = 'hidden';
	prefillFromForm();
	setState('ready');
	// Focus first empty input
	setTimeout(() => {
		const name = document.getElementById('picName');
		const phone = document.getElementById('picPhone');
		if (name && !name.value) name.focus();
		else if (phone && !phone.value) phone.focus();
	}, 100);
}

function closeModal() {
	const overlay = document.getElementById('probotInstantCallOverlay');
	if (!overlay) return;
	overlay.hidden = true;
	document.body.style.overflow = '';
	stopPolling();
}

function prefillFromForm() {
	const nameEl = document.getElementById('picName');
	const phoneEl = document.getElementById('picPhone');
	if (!nameEl || !phoneEl) return;
	// v1.1.96 — Field IDs from templates/partials/p1-contact.php :
	//   - #inFname : input first name (autocomplete given-name)
	//   - #inTel : input phone tel (autocomplete tel)
	// On p2 Robots / p3 Date (where the CTA is shown), the user has already
	// filled p1 Contact, so these inputs should have values.
	const formName = document.getElementById('inFname')
		|| document.querySelector('#f-fname input, #firstName, [name="firstName"]');
	const formPhone = document.getElementById('inTel')
		|| document.querySelector('#f-tel input[type=tel], #phone, [name="phone"]');
	const formEmail = document.getElementById('inEmail')
		|| document.querySelector('#f-email input[type=email], [name="email"]');
	if (formName && formName.value && !nameEl.value) {
		nameEl.value = formName.value.trim();
		console.log('[probot-instant-call] prefilled name from form');
	}
	if (formPhone && formPhone.value && !phoneEl.value) {
		phoneEl.value = formPhone.value.trim();
		console.log('[probot-instant-call] prefilled phone from form');
	}
	// v1.1.101 — Email cache pour le fallback booking (qui le requiert)
	if (formEmail && formEmail.value) {
		prefilledEmail = formEmail.value.trim();
		console.log('[probot-instant-call] cached email from form for booking fallback');
	}
}

function showError(msg) {
	const el = document.getElementById('picError');
	if (el) { el.hidden = false; el.textContent = msg; }
}
function clearError() {
	const el = document.getElementById('picError');
	if (el) { el.hidden = true; el.textContent = ''; }
}

async function startCall() {
	console.log('[probot-instant-call] startCall() fired');
	clearError();
	// v1.1.97 — Null-safe element access. Si le modal partial n'est pas
	// charge sur la page (ex: shortcode mis sur une page custom sans
	// l'include instant-call-modal.php), on doit ne PAS crasher avec
	// "Cannot read properties of null (reading 'value')".
	const nameEl = document.getElementById('picName');
	const phoneEl = document.getElementById('picPhone');
	const subjectEl = document.getElementById('picSubject');
	if (!nameEl || !phoneEl) {
		console.error('[probot-instant-call] CRITICAL : picName ou picPhone element manque dans le DOM. Le partial templates/partials/instant-call-modal.php est probablement pas inclus sur cette page. Verifier que form.php OU form-service.php include le partial. Reload la page (Ctrl+Shift+R) pour vider le cache du bundle JS.');
		alert('Erreur : popup non initialise correctement. Reload la page (Ctrl+Shift+R) et reessayer. Si le probleme persiste, contactez l\'administrateur.');
		return;
	}
	const name = (nameEl.value || '').trim();
	const phone = (phoneEl.value || '').trim();
	const subject = (subjectEl && subjectEl.value || '').trim();
	console.log('[probot-instant-call] form values:', { name, phone: phone ? '(set)' : '(empty)' });
	if (!name) { showError(t('Ton prenom est requis.', 'Your first name is required.')); return; }
	if (!phone || !/[0-9]{10,}/.test(phone.replace(/\D/g, ''))) {
		showError(t('Numero de telephone invalide (au moins 10 chiffres).', 'Invalid phone number (at least 10 digits).'));
		return;
	}
	const btn = document.getElementById('picStartCall');
	if (btn) btn.disabled = true;
	setState('calling');
	setStatusText(t('Verification de la disponibilite...', 'Checking availability...'));
	// Step 1: check rep availability first to skip Twilio cost if offline
	let availResp;
	try {
		console.log('[probot-instant-call] GET /meeting-instant-available');
		availResp = await api('meeting-instant-available', { method: 'GET' });
		console.log('[probot-instant-call] availability response:', availResp);
	} catch (e) {
		console.warn('[probot-instant-call] availability fetch threw', e);
		// Network error — skip directly to fallback
		await loadFallbackBooking();
		return;
	}
	if (availResp.status >= 400 || !availResp.data || !availResp.data.available) {
		console.log('[probot-instant-call] rep not available → fallback booking');
		// Rep not available -> skip Twilio, go direct to booking
		await loadFallbackBooking();
		return;
	}
	// Step 2: rep available, launch Twilio
	setStatusText(t('Demarrage de l\'appel...', 'Starting the call...'));
	let callResp;
	try {
		console.log('[probot-instant-call] POST /meeting-instant-call');
		// v1.1.101 — Normalize phone en E.164 avant l'envoi (Twilio 400 sinon)
		const phoneE164 = normalizeToE164(phone);
		console.log('[probot-instant-call] phone normalize:', phone, '->', phoneE164);
		// v1.1.105 SECURITY — Fetch reCAPTCHA token (vide si pas configure).
		const recaptchaToken = await fetchBotToken('meeting_instant_call');
		callResp = await api('meeting-instant-call', {
			method: 'POST',
			body: JSON.stringify({
				name: name,
				email: prefilledEmail || '', // best effort si dispo
				phone_e164: phoneE164,
				company: '',
				subject: subject || t('Appel instant depuis le site', 'Instant call from website'),
				recaptcha_token: recaptchaToken,
			}),
		});
		console.log('[probot-instant-call] call response:', callResp);
	} catch (e) {
		console.warn('[probot-instant-call] call fetch threw', e);
		setState('ready');
		if (btn) btn.disabled = false;
		showError(t('Erreur reseau. Reessaie. (Voir console)', 'Network error. Try again. (See console)'));
		return;
	}
	if (callResp.status >= 400 || !callResp.data || !callResp.data.call_sid) {
		// Failed to launch call -> show detailed error AND offer fallback.
		// Common causes: bot protection requires reCAPTCHA token (403), rate limit
		// (429), or Twilio config incomplete (500).
		const errMsg = (callResp.data && (callResp.data.message || callResp.data.error)) || 'HTTP ' + callResp.status;
		console.warn('[probot-instant-call] call rejected by server:', errMsg, callResp);
		setState('ready');
		if (btn) btn.disabled = false;
		showError(t(
			'Impossible de lancer l\'appel : ' + errMsg + '. On te propose des creneaux a la place.',
			'Cannot start the call: ' + errMsg + '. Offering time slots instead.'
		));
		// Auto-fallback to booking after 2s so user sees the error then booking opens
		setTimeout(() => { loadFallbackBooking(); }, 2000);
		return;
	}
	currentCallSid = callResp.data.call_sid;
	console.log('[probot-instant-call] call started, sid=' + currentCallSid);
	setStatusText(t('Appel en cours, on attend la reponse...', 'Call in progress, waiting for answer...'));
	startPolling();
}

function setStatusText(txt) {
	const el = document.getElementById('picCallStatus');
	if (el) el.textContent = txt;
}

function startPolling() {
	pollStartTime = Date.now();
	pollTimer = setInterval(pollStatus, POLL_INTERVAL_MS);
	pollStatus(); // immediate first poll
}

function stopPolling() {
	if (pollTimer) {
		clearInterval(pollTimer);
		pollTimer = null;
	}
}

async function pollStatus() {
	if (!currentCallSid) { stopPolling(); return; }
	const elapsed = Date.now() - pollStartTime;
	if (elapsed > POLL_TIMEOUT_MS) {
		// Timeout — assume no-answer, switch to fallback
		stopPolling();
		setStatusText(t('Pas de reponse. On te propose des creneaux...', 'No answer. Offering time slots...'));
		await loadFallbackBooking();
		return;
	}
	let resp;
	try {
		resp = await api('meeting-instant-call-status?call_sid=' + encodeURIComponent(currentCallSid), { method: 'GET' });
	} catch (e) {
		// Network blip — keep polling, don't bail
		return;
	}
	if (resp.status >= 400 || !resp.data) return;
	const status = resp.data.status || 'pending';
	// Update status text
	const statusLabels = {
		pending: t('Connexion a Twilio...', 'Connecting to Twilio...'),
		initiated: t('Appel initie...', 'Call initiated...'),
		ringing: t('Le telephone de l\'expert sonne...', 'Expert\'s phone is ringing...'),
		answered: t('L\'expert a decroche ! On te connecte...', 'Expert answered! Connecting you...'),
		'in-progress': t('Connexion en cours...', 'Connecting...'),
	};
	if (statusLabels[status]) setStatusText(statusLabels[status]);
	if (resp.data.bridge_success) {
		// Rep answered -> success
		stopPolling();
		setState('success');
		return;
	}
	if (resp.data.is_terminal) {
		// Call ended (failed, no-answer, busy, canceled, or completed without bridge)
		// If completed: bridge_success should already be true (handled above).
		// If we reach here with is_terminal=true and bridge_success=false, it's a failure.
		stopPolling();
		setStatusText(t('Pas de reponse. On te propose des creneaux...', 'No answer. Offering time slots...'));
		await loadFallbackBooking();
	}
}

// ─── FALLBACK BOOKING STATE ────────────────────────────────────────────────
async function loadFallbackBooking() {
	setState('fallback');
	bookingState.viewMonth = new Date();
	bookingState.viewMonth.setDate(1);
	bookingState.selectedDate = null;
	bookingState.selectedSlot = null;
	await fetchAvailability();
	renderCalendar();
}

async function fetchAvailability() {
	// Fetch 90 days of availability starting today
	const from = isoDate(new Date());
	const to = new Date();
	to.setDate(to.getDate() + 90);
	const toStr = isoDate(to);
	try {
		const resp = await api(`meeting-availability?start=${from}&end=${toStr}`, { method: 'GET' });
		if (resp.status < 400 && resp.data) {
			bookingState.availability = resp.data.slots || resp.data || {};
			return;
		}
	} catch (e) {
		// Fallback: empty availability — calendar will show no slots
	}
	bookingState.availability = {};
}

function renderCalendar() {
	const grid = document.getElementById('picCalGrid');
	const title = document.getElementById('picCalTitle');
	if (!grid || !title) return;
	grid.innerHTML = '';
	const v = bookingState.viewMonth;
	title.textContent = `${monthName(v.getMonth())} ${v.getFullYear()}`;
	// DOW headers
	const dows = dowShort();
	for (let i = 0; i < 7; i++) {
		const el = document.createElement('div');
		el.className = 'pic-cal-dow';
		el.textContent = dows[i];
		grid.appendChild(el);
	}
	// Cells
	const first = new Date(v.getFullYear(), v.getMonth(), 1);
	const dowFirst = first.getDay();
	const start = new Date(first);
	start.setDate(1 - dowFirst);
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	for (let w = 0; w < 6; w++) {
		for (let d = 0; d < 7; d++) {
			const cell = document.createElement('button');
			cell.type = 'button';
			cell.className = 'pic-cal-cell';
			const dt = new Date(start);
			dt.setDate(start.getDate() + w * 7 + d);
			cell.textContent = dt.getDate();
			const inMonth = dt.getMonth() === v.getMonth();
			const iso = isoDate(dt);
			const slots = (bookingState.availability && bookingState.availability[iso]) || [];
			if (!inMonth) {
				cell.classList.add('muted');
				cell.disabled = true;
			} else if (dt < today) {
				cell.classList.add('disabled');
				cell.disabled = true;
			} else if (slots.length === 0) {
				cell.classList.add('disabled');
				cell.disabled = true;
				cell.title = t('Aucun creneau dispo', 'No slots available');
			} else {
				if (bookingState.selectedDate === iso) cell.classList.add('selected');
				cell.dataset.date = iso;
			}
			grid.appendChild(cell);
		}
	}
}

function renderSlots() {
	const wrap = document.getElementById('picSlots');
	const list = document.getElementById('picSlotsList');
	if (!wrap || !list) return;
	if (!bookingState.selectedDate) {
		wrap.hidden = true;
		return;
	}
	const slots = (bookingState.availability && bookingState.availability[bookingState.selectedDate]) || [];
	list.innerHTML = '';
	slots.forEach((slot) => {
		const btn = document.createElement('button');
		btn.type = 'button';
		btn.className = 'pic-slot-btn';
		btn.textContent = slot;
		btn.dataset.slot = slot;
		list.appendChild(btn);
	});
	wrap.hidden = false;
}

async function bookSlot(date, slot) {
	const name = (document.getElementById('picName').value || '').trim();
	const phone = (document.getElementById('picPhone').value || '').trim();
	const subjectEl = document.getElementById('picSubject');
	const subject = (subjectEl && subjectEl.value || '').trim();
	if (!name || !phone) {
		const err = document.getElementById('picBookingError');
		if (err) { err.hidden = false; err.textContent = t('Nom et telephone requis pour confirmer.', 'Name and phone required to confirm.'); }
		return;
	}
	const allBtns = document.querySelectorAll('.pic-slot-btn');
	allBtns.forEach((b) => b.classList.add('booking'));
	const slotIso = date + 'T' + slot;
	let resp;
	try {
		// v1.1.101 — phone normalize + champ phone_e164 (pas phone) + email
		// pre-rempli depuis form Step 1 (Meeting::book exige email non vide).
		const phoneE164 = normalizeToE164(phone);
		// v1.1.135 — FIX : /meeting-book exige un token anti-bot (action
		// 'meeting_book', cf. guard_rest côté serveur). Sans lui, la réservation
		// depuis le calendrier de secours échouait avec « Vérification anti-bot
		// échouée ». L'appel /meeting-instant-call plus haut le faisait déjà ;
		// celui-ci l'avait oublié.
		const bookToken = await fetchBotToken('meeting_book');
		resp = await api('meeting-book', {
			method: 'POST',
			body: JSON.stringify({
				name: name,
				email: prefilledEmail || '',
				phone_e164: phoneE164,
				company: '',
				subject: subject || t('Appel demande depuis le site', 'Call requested from website'),
				slot_iso: slotIso,
				recaptcha_token: bookToken,
			}),
		});
	} catch (e) {
		allBtns.forEach((b) => b.classList.remove('booking'));
		const err = document.getElementById('picBookingError');
		if (err) { err.hidden = false; err.textContent = t('Erreur reseau. Reessaie.', 'Network error. Try again.'); }
		return;
	}
	if (resp.status >= 400) {
		allBtns.forEach((b) => b.classList.remove('booking'));
		const err = document.getElementById('picBookingError');
		const msg = (resp.data && (resp.data.message || resp.data.error)) || t('Erreur lors de la reservation.', 'Booking error.');
		if (err) { err.hidden = false; err.textContent = msg; }
		return;
	}
	// Success
	setState('booked');
	const det = document.getElementById('picBookedDetails');
	if (det) {
		const dt = new Date(slotIso);
		const fmt = lang() === 'en'
			? dt.toLocaleString('en-US', { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })
			: dt.toLocaleString('fr-CA', { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' });
		det.textContent = t('Confirme pour : ', 'Confirmed for: ') + fmt;
	}
}

// ─── EVENT DELEGATION ──────────────────────────────────────────────────────
function initInstantCall() {
	console.log('[probot-instant-call] initInstantCall() — event listeners attached');
	// v1.1.98 — Diagnostic au load : check si le modal partial est dans le DOM.
	// Si NON, le partial n'a pas ete inclus OU return early (ex: meeting_page_url
	// non configure dans Settings, OU bi/fr closures pas defined dans le scope
	// du include). Aide a diagnostiquer "picName est null" avant que le user
	// clique le bouton.
	setTimeout(() => {
		const overlay = document.getElementById('probotInstantCallOverlay');
		const picName = document.getElementById('picName');
		const picPhone = document.getElementById('picPhone');
		const hasTrigger = document.querySelector('[data-instant-call-trigger]');
		console.log('[probot-instant-call] DOM check:', {
			'modal overlay (#probotInstantCallOverlay)': overlay ? 'PRESENT' : 'MISSING',
			'input #picName': picName ? 'PRESENT' : 'MISSING',
			'input #picPhone': picPhone ? 'PRESENT' : 'MISSING',
			'trigger [data-instant-call-trigger]': hasTrigger ? 'PRESENT' : 'MISSING',
		});
		if (hasTrigger && !overlay) {
			console.error('[probot-instant-call] CRITICAL : le bouton trigger est sur la page MAIS le modal partial (templates/partials/instant-call-modal.php) n\'a PAS rendu. Causes probables :\n  1. Settings -> Integrations -> Meeting -> "URL page meeting" est vide (meeting_page_url non configure)\n  2. La page utilise un shortcode custom qui n\'include pas le modal\n  3. Cache PHP : flush opcache + reload\n  4. Cache navigateur : Ctrl+Shift+R');
		}
	}, 100);
	document.addEventListener('click', (e) => {
		const trigger = e.target.closest('[data-instant-call-trigger]');
		if (trigger) {
			e.preventDefault();
			openModal();
			return;
		}
		if (e.target.matches('[data-pic-close]') || e.target.classList.contains('probot-instant-call-overlay')) {
			closeModal();
			return;
		}
		if (e.target.id === 'picStartCall' || e.target.closest('#picStartCall')) {
			startCall();
			return;
		}
		if (e.target.matches('[data-pic-cal-prev]')) {
			bookingState.viewMonth = new Date(bookingState.viewMonth.getFullYear(), bookingState.viewMonth.getMonth() - 1, 1);
			renderCalendar();
			return;
		}
		if (e.target.matches('[data-pic-cal-next]')) {
			bookingState.viewMonth = new Date(bookingState.viewMonth.getFullYear(), bookingState.viewMonth.getMonth() + 1, 1);
			renderCalendar();
			return;
		}
		const cell = e.target.closest('.pic-cal-cell');
		if (cell && cell.dataset.date) {
			bookingState.selectedDate = cell.dataset.date;
			renderCalendar();
			renderSlots();
			return;
		}
		const slotBtn = e.target.closest('.pic-slot-btn');
		if (slotBtn && !slotBtn.classList.contains('booking')) {
			bookSlot(bookingState.selectedDate, slotBtn.dataset.slot);
			return;
		}
	});
	// ESC to close
	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape') {
			const overlay = document.getElementById('probotInstantCallOverlay');
			if (overlay && !overlay.hidden) closeModal();
		}
	});
}

export { initInstantCall };
